import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { map, switchMap, tap, catchError } from 'rxjs/operators';
import { Conversation } from '@app-libs/model';
import { IConversationManager, CreateConversationParams } from './conversation.interfaces';
import { ConversationsApiService } from '../../api/conversations-api.service';
import { AuthService } from '../../auth/auth.service';
import { ChatStateService } from '../chat-state.service';

/**
 * ConversationManager - Handles conversation CRUD operations
 *
 * This service handles all conversation operations, migrated from ChatApiService
 * with the exact same logic to preserve functionality.
 */
@Injectable({
  providedIn: 'root',
})
export class ConversationManager implements IConversationManager {
  constructor(
    private conversationsApiService: ConversationsApiService,
    private authService: AuthService,
    private chatStateService: ChatStateService,
  ) {}

  /**
   * Create a new conversation
   * (Migrated from ChatApiService.createConversation - exact same logic)
   */
  createConversation(params: CreateConversationParams): Observable<Conversation> {
    // Generate title from initial message content
    const title = params.initialMessage
      ? params.initialMessage.charAt(0).toUpperCase() + params.initialMessage.substring(1, 80)
      : 'New conversation';

    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      return throwError(() => new Error('User not authenticated'));
    }

    return from(Promise.resolve(userId)).pipe(
      switchMap((userId) =>
        from(this.authService.getProfile()).pipe(
          map((profile) => ({
            user_id: userId,
            title,
            assistant_id: this.chatStateService.assistant.assistant_id || null,
            thread_id: this.chatStateService.threadId || null,
            last_message_date: new Date().toISOString(),
            workspace_id: profile?.selected_workspace || null,
          })),
        ),
      ),
      switchMap((conversationData) =>
        this.conversationsApiService.createConversation(conversationData),
      ),
      tap((conversation) => {
        // Update state with new conversation using simplified API
        this.chatStateService.conversation = {
          ...this.chatStateService.conversation,
          conversation_id: conversation.conversation_id,
        };
      }),
    );
  }

  /**
   * Update an existing conversation
   * (Migrated from ChatApiService.updateConversation - exact same logic)
   */
  updateConversation(
    conversationId: string,
    updates: Partial<Conversation>,
  ): Observable<Conversation> {
    return this.conversationsApiService.updateConversation(conversationId, updates).pipe(
      map((success) => {
        if (success) {
          // Return the updated conversation (for interface compatibility)
          return { ...updates, conversation_id: conversationId } as Conversation;
        }
        throw new Error('Failed to update conversation');
      }),
      catchError((error) => {
        console.error('Failed to update conversation:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Ensure a conversation exists, creating if necessary
   * (Migrated from ChatApiService.ensureConversationExists - exact same logic)
   */
  ensureConversationExists(initialContent?: string): Observable<Conversation> {
    // Check if conversation already exists
    if (this.chatStateService.conversation.conversation_id) {
      // Return existing conversation (need to fetch it)
      return from(Promise.resolve(this.chatStateService.conversation as Conversation));
    }

    // Create new conversation
    return this.createConversation({
      initialMessage: initialContent || '',
      workspaceId: this.chatStateService.workspaceId,
      assistantId: this.chatStateService.assistant.assistant_id,
    });
  }

  /**
   * Get conversation by ID
   */
  getConversation(conversationId: string): Observable<Conversation> {
    return this.conversationsApiService.getConversationById(conversationId).pipe(
      catchError((error) => {
        console.error('Failed to get conversation:', error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Delete a conversation
   */
  deleteConversation(conversationId: string): Observable<void> {
    return this.conversationsApiService.softDeleteConversation(parseInt(conversationId)).pipe(
      map(() => void 0),
      catchError((error) => {
        console.error('Failed to delete conversation:', error);
        return throwError(() => error);
      }),
    );
  }
}
